import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList,
  BreadcrumbPage, BreadcrumbSeparator,
} from "../components/ui/breadcrumb";
import { useCart } from "../context/CartContext";
import { checkoutWithWooCommerce } from "../services/woocommerce";

const Checkout = () => {
  const { items, clearCart } = useCart();
  const [isSyncing, setIsSyncing] = useState(true);
  const [showIframe, setShowIframe] = useState(false);
  const [syncError, setSyncError] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(850);

  useEffect(() => {
    const handleMessage = (event) => {
      if (
        event.data &&
        event.data.type === "SET_CHECKOUT_HEIGHT" &&
        event.data.height >= 400 &&
        event.data.height <= 2500
      ) {
        setIframeHeight(event.data.height);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    let mounted = true;
    const syncCart = async () => {
      if (items.length === 0 && !showIframe) {
        setIsSyncing(false);
        return;
      }
      
      try {
        await checkoutWithWooCommerce(items);
        if (mounted) {
          setIsSyncing(false);
          setShowIframe(true);
          // Only clear cart after successful sync
          clearCart();
        }
      } catch (err) {
        if (mounted) {
          toast.error("Erro ao sincronizar carrinho com a loja: " + err.message);
          setIsSyncing(false);
          setSyncError(true);
        }
      }
    };

    // Only run if we have items and haven't synced yet
    if (items.length > 0 && isSyncing) {
      syncCart();
    }
    
    return () => { mounted = false; };
  }, [items, isSyncing, showIframe, clearCart]);

  if (items.length === 0 && !showIframe) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-20 text-center">
        <h1 className="text-2xl font-bold text-slate-900">Checkout</h1>
        <p className="mt-2 text-slate-500">Seu carrinho está vazio.</p>
        <Button asChild className="mt-6 bg-brand hover:bg-brand-hover"><Link to="/loja">Ir às compras</Link></Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem><BreadcrumbLink asChild><Link to="/carrinho">Carrinho</Link></BreadcrumbLink></BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem><BreadcrumbPage>Checkout</BreadcrumbPage></BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex items-center gap-4 mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Finalizar compra</h1>
      </div>

      {isSyncing ? (
        <div className="w-full bg-white border border-slate-200 rounded-lg flex flex-col items-center justify-center h-[50vh] text-center p-6">
          <Loader2 className="h-10 w-10 animate-spin text-brand mb-4" />
          <h2 className="text-lg font-semibold text-slate-900 mb-1">Preparando ambiente seguro...</h2>
          <p className="text-sm text-slate-500">Sincronizando seus produtos com o nosso checkout Mercado Pago.</p>
          <div className="mt-6 bg-slate-50 border border-slate-200 rounded-lg p-3 inline-flex items-center gap-2 text-xs text-slate-700">
            <ShieldCheck className="h-4 w-4 text-brand" />
            Criptografia ponta a ponta
          </div>
        </div>
      ) : syncError ? (
        <div className="w-full bg-red-50 border border-red-200 rounded-lg flex flex-col items-center justify-center h-[50vh] text-center p-6">
          <h2 className="text-lg font-bold text-red-700 mb-2">Ops, algo deu errado.</h2>
          <p className="text-sm text-red-600 mb-6">Não foi possível iniciar a sessão de pagamento.</p>
          <Button onClick={() => window.location.reload()} className="bg-red-600 hover:bg-red-700 text-white">
            Tentar novamente
          </Button>
        </div>
      ) : (
        <div className="w-full transition-all">
           <iframe 
             src="/wp/index.php/finalizar-compra/"
             style={{ width: "100%", height: `${iframeHeight}px`, border: "none", display: "block" }}
             scrolling="no"
             title="WooCommerce Checkout"
             sandbox="allow-scripts allow-same-origin allow-forms allow-top-navigation allow-popups"
           />
        </div>
      )}
    </div>
  );
};

export default Checkout;
